import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Group, Loader2, Palette, Rocket, Save, Send, Store, Ungroup, Upload } from 'lucide-react'

import type { CanvasSaveStatus } from '@/features/studio/hooks/useCanvasGraphSave'
import { Button } from '@/shared/components/ui/button'

export function CanvasTopBar({
  title,
  returnPath,
  saveStatus,
  onBack,
  onSave,
  stylePickerOpen,
  onToggleStylePicker,
  onRegister,
  registerDisabled,
  canGroup,
  canUngroup,
  onGroup,
  onUngroup,
  hideRegister,
  onList,
  listDisabled,
  listStatusLabel,
  showSubmitReview,
  onSubmitReview,
  submitReviewBusy,
  onSyncWorks,
  onPublishWorks,
  publishWorksBusy,
  publishWorksDisabled,
}: {
  title: string
  returnPath: string
  saveStatus: CanvasSaveStatus
  onBack?: () => void
  onSave?: () => void
  stylePickerOpen?: boolean
  onToggleStylePicker?: () => void
  onRegister: () => void
  registerDisabled?: boolean
  canGroup?: boolean
  canUngroup?: boolean
  onGroup?: () => void
  onUngroup?: () => void
  hideRegister?: boolean
  onList?: () => void
  listDisabled?: boolean
  /** 当前画布版本已挂牌时展示状态，替代可点击的挂牌按钮 */
  listStatusLabel?: string | null
  /** 分镜画布：生成完成后在此提交审片 */
  showSubmitReview?: boolean
  onSubmitReview?: () => void
  submitReviewBusy?: boolean
  /** 自由创作：同步到作品广场 */
  onSyncWorks?: () => void
  onPublishWorks?: () => void
  publishWorksBusy?: boolean
  publishWorksDisabled?: boolean
}) {
  const { t } = useTranslation()
  const saving = saveStatus === 'saving'

  const saveLabel =
    saveStatus === 'saving'
      ? t('canvas.saving')
      : saveStatus === 'conflict'
        ? t('canvas.conflict')
      : saveStatus === 'dirty'
        ? t('canvas.unsaved')
        : saveStatus === 'error'
          ? t('canvas.saveFailed')
          : saveStatus === 'saved'
            ? t('canvas.saved')
            : t('canvas.save')

  return (
    <header className="studio-canvas-topbar">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="studio-canvas-topbar__back h-auto px-0"
        title={returnPath}
      >
        {t('canvas.back')}
      </Button>
      <div className="min-w-0 flex-1 truncate studio-canvas-topbar__title">
        {title || t('canvas.untitled')}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={saving}
        onClick={onSave}
        className={clsx(
          'studio-canvas-topbar__style inline-flex items-center gap-1.5 disabled:opacity-40',
          saveStatus === 'saved' && 'studio-canvas-topbar__style--active',
          (saveStatus === 'dirty' || saveStatus === 'conflict') && 'text-amber-300/90',
          saveStatus === 'error' && 'text-rose-300/90',
        )}
      >
        {saving ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Save className="h-3.5 w-3.5" />
        )}
        {saveLabel}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onToggleStylePicker}
        className={clsx(
          'studio-canvas-topbar__style inline-flex items-center gap-1.5',
          stylePickerOpen && 'studio-canvas-topbar__style--active',
        )}
      >
        <Palette className="h-3.5 w-3.5" />
        {t('canvas.theme.openPicker')}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={!canGroup}
        onClick={onGroup}
        title={t('canvas.groupShortcut')}
        className="studio-canvas-topbar__style inline-flex items-center gap-1.5 disabled:opacity-40"
      >
        <Group className="h-3.5 w-3.5" />
        {t('canvas.groupCreate')}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={!canUngroup}
        onClick={onUngroup}
        title={t('canvas.ungroupShortcut')}
        className="studio-canvas-topbar__style inline-flex items-center gap-1.5 disabled:opacity-40"
      >
        <Ungroup className="h-3.5 w-3.5" />
        {t('canvas.groupUngroup')}
      </Button>
      {onList ? (
        listStatusLabel ? (
          <span
            className="studio-canvas-topbar__style inline-flex max-w-[9rem] items-center gap-1.5 truncate rounded-md bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-300 ring-1 ring-emerald-500/25"
            title={listStatusLabel}
          >
            <Store className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{listStatusLabel}</span>
          </span>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={listDisabled}
            onClick={onList}
            title={listDisabled ? t('creative.listing.disabledHint') : undefined}
            className="studio-canvas-topbar__style inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <Store className="h-3.5 w-3.5" />
            {t('creative.listing.short')}
          </Button>
        )
      ) : null}
      {onSyncWorks ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={publishWorksBusy || publishWorksDisabled}
          onClick={onSyncWorks}
          className="studio-canvas-topbar__style inline-flex items-center gap-1.5 disabled:opacity-40"
        >
          {publishWorksBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          {t('timeline.syncWorks')}
        </Button>
      ) : null}
      {onPublishWorks ? (
        <Button
          type="button"
          variant="default"
          size="sm"
          disabled={publishWorksBusy || publishWorksDisabled}
          onClick={onPublishWorks}
          className="studio-canvas-topbar__cta inline-flex items-center gap-1.5"
        >
          {publishWorksBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Rocket className="h-3.5 w-3.5" />
          )}
          {t('timeline.publishWorks')}
        </Button>
      ) : null}
      {showSubmitReview ? (
        <Button
          type="button"
          variant="default"
          size="sm"
          disabled={submitReviewBusy || registerDisabled}
          onClick={onSubmitReview}
          className="studio-canvas-topbar__cta inline-flex items-center gap-1.5"
        >
          {submitReviewBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          {t('reviews.submitReview')}
        </Button>
      ) : null}
      {!hideRegister ? (
      <Button
        type="button"
        variant={showSubmitReview ? 'secondary' : 'default'}
        size="sm"
        disabled={registerDisabled}
        onClick={onRegister}
        className={showSubmitReview ? 'studio-canvas-topbar__style' : 'studio-canvas-topbar__cta'}
      >
        {t('canvas.registerReturn')}
      </Button>
      ) : null}
    </header>
  )
}
